'use client';

import {
	Autocomplete,
	Box,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { gql, useLazyQuery, useQuery } from '@parkchirp/api-client';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { GET_ORGANIZATION_SETTINGS_BY_FACILITY_ID } from '../../queries/facility';
import { Dayjs, dayjs } from '../../utils';
import { ControlledTooltip } from '../toolbar';

export const formatTerminationDate = (date) => {
	if (date) {
		if (typeof date === 'string') {
			return dayjs(date, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY');
		}

		if (date instanceof Date) {
			return dayjs(date).format('MM/DD/YYYY');
		}
	}

	return '';
};

const GET_RATE = gql`
    query getRate(
        $rateId: String!,
        $tenantSubscriptionId: String
    ) {
        getRate(
            rateId: $rateId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id,
            name,
            sublotId
            tenantRateSublotSelections {
                sublot {
                    id
                    name
                    primaryLot
                }
            }
        }
    }
`;

const GET_SUBSCRIPTION_RATE = gql`
    query getRatesBySubscription(
        $subscriptionId: String!,
        $tenantSubscriptionId: String,
    ) {
        getRatesBySubscription(
            subscriptionId: $subscriptionId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id
            active
            name
            activeDate
            childRateStartDate
            childRateEndDate
            sublotId
            tenantRateSublotSelections {
                sublot {
                    id
                    name
                    primaryLot
                }
            }
            isSoldOut
        }
    }
`;

const LIST_DEFAULT_RATES_BY_SUBSCRIPTION = gql`
    query listDefaultRatesBySubscription(
        $subscriptionId: String!,
        $facilityId: String!,
    ) {
        listDefaultRatesBySubscription(
            subscriptionId: $subscriptionId,
            facilityId: $facilityId,
        ) {
            id
            name
            sublotId
            isSoldOut
            tenantRateSublotSelections {
                sublot {
                    id
                    name
                    primaryLot
                }
            }
        }
    }
`;

const GET_PARKER_TERMINATION_DATE = gql`
    query getParkerTerminationDate(
        $parkerId: String!,
        $tenantSubscriptionId: String
    ) {
        getParkerTerminationDate(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

type RateData = {
	getRate: {
		id: string;
		name: string;
		sublotId: number;
		isSoldOut: boolean;
		tenantRateSublotSelections: {
			id: number;
			sublot: {
				id: number;
				name: string;
				primaryLot: boolean;
			};
		}[];
	};
};

type TerminationDateData = {
	getParkerTerminationDate: string;
};

type SubscriptionRateData = {
	getRatesBySubscription: {
		id: string;
		name: string;
		active: boolean;
		activeDate: string;
		sublotId: number;
		childRateStartDate?: string;
		childRateEndDate?: string;
		isSoldOut: boolean;
		tenantRateSublotSelections: {
			id: number;
			sublot: {
				id: number;
				name: string;
				primaryLot: boolean;
			};
		}[];
	}[];
};

type SubscriptionDefaultRateData = {
	listDefaultRatesBySubscription: {
		id: string;
		name: string;
		sublotId: number;
		isSoldOut: boolean;
		tenantRateSublotSelections: {
			id: number;
			sublot: {
				id: number;
				name: string;
				primaryLot: boolean;
			};
		}[];
	}[];
};

interface RateProps {
	parkerId: string;
	subscriptionId: string | string[];
	newRateId: string;
	setNewRateId: (rateId: string | null) => void;
	newBillingStartDate: Dayjs;
	setNewBillingStartDate: (billingStartDate: Dayjs | null) => void;
	setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
	isEditMode: boolean;
	sublots: {
		id: number;
		name: string;
		primaryLot: boolean;
	}[];
	sublotId: string;
	setSublotId: Dispatch<SetStateAction<string>>;
	suspensionStatus: string;
	terminationStatus: string;
	tenantSubscriptionId?: string;
	facilityTimezone: string;
	parkerLoading: boolean;
	usesDefaultRates: boolean;
	monthlyFeePrice: number | null;
	setMonthlyFeePrice: Dispatch<SetStateAction<number | null>>;
	newMode: boolean;
	facilityId: string;
	minStartDate: Dayjs;
	facilityLoading: boolean;
	setSelectedRateIsSoldOut: Dispatch<SetStateAction<boolean>>;
	selectedRateIsSoldOut: boolean;
}

function isStartDateInPreviousParkingPeriod(startDate, timezone): boolean {
	const startOfMonth = dayjs().tz(timezone, true).startOf('month');
	const billingStartDate = dayjs(startDate).tz(timezone, true);

	return billingStartDate.isBefore(startOfMonth);
}

function isPendingAndNotLoading(parkerLoading, parkerIsPending) {
	return !parkerLoading && parkerIsPending;
}

export const RateDetail: FC<RateProps> = ({
	parkerId,
	subscriptionId,
	newRateId,
	setNewRateId,
	newBillingStartDate,
	setNewBillingStartDate,
	setHasUnsavedChanges,
	isEditMode,
	sublots = [],
	sublotId,
	setSublotId,
	suspensionStatus,
	terminationStatus,
	tenantSubscriptionId,
	facilityTimezone,
	parkerLoading,
	usesDefaultRates,
	setMonthlyFeePrice,
	monthlyFeePrice,
	newMode,
	facilityId,
	minStartDate,
	facilityLoading,
	setSelectedRateIsSoldOut,
	selectedRateIsSoldOut,
}) => {
	const [getSubscriptionRates, { data: newRateData }] = useLazyQuery<SubscriptionRateData>(GET_SUBSCRIPTION_RATE);
	const [listDefaultRatesBySubscription, { data: defaultRatesData }] = useLazyQuery<SubscriptionDefaultRateData>(
		LIST_DEFAULT_RATES_BY_SUBSCRIPTION,
	);
	const [getRate, { data: rateData }] = useLazyQuery<RateData>(GET_RATE);

	const parkerPendingOrNotInvoiced = ['NOT_INVOICED', 'PENDING'].includes(terminationStatus);
	const parkerIsPending = suspensionStatus === 'PENDING';
	const [sublotState, setSublotState] = useState([...sublots]);

	useEffect(() => {
		setSublotState((currentSublots) => {
			const sublotsAreUnchanged =
				currentSublots.length === sublots.length &&
				currentSublots.every(
					(currentSublot, index) =>
						currentSublot.id === sublots[index].id &&
						currentSublot.name === sublots[index].name &&
						currentSublot.primaryLot === sublots[index].primaryLot,
				);

			return sublotsAreUnchanged ? currentSublots : [...sublots];
		});
	}, [sublots]);

	const [getParkerTerminationDate, { data: terminationData }] =
		useLazyQuery<TerminationDateData>(GET_PARKER_TERMINATION_DATE);

	const { data: orgSettings } = useQuery(GET_ORGANIZATION_SETTINGS_BY_FACILITY_ID, {
		variables: {
			facilityId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !facilityId,
	});
	const prohibitPreviousParkingPeriodParkers =
		orgSettings?.getOrganizationSettingsByFacilityId?.prohibitPreviousParkingPeriodParkers ?? false;

	useEffect(() => {
		if (usesDefaultRates && newMode) {
			if (!defaultRatesData) {
				listDefaultRatesBySubscription({
					variables: { subscriptionId, facilityId },
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
					fetchPolicy: 'network-only',
				});
			}
			return;
		}

		if (!usesDefaultRates && !facilityLoading) {
			if (!newRateData) {
				getSubscriptionRates({
					variables: { subscriptionId, tenantSubscriptionId },
					context: {
						apiVersion: 'v2',
						appName: process.env.NEXT_PUBLIC_APP_NAME,
					},
					fetchPolicy: 'network-only',
				});
			}
			return;
		}

		if (newRateId && !rateData) {
			getRate({
				variables: { rateId: newRateId, tenantSubscriptionId },
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				fetchPolicy: 'network-only',
			});
		}
	}, [
		parkerPendingOrNotInvoiced,
		usesDefaultRates,
		newMode,
		newRateId,
		subscriptionId,
		tenantSubscriptionId,
		facilityId,
		facilityLoading,
		listDefaultRatesBySubscription,
		getSubscriptionRates,
		getRate,
		defaultRatesData,
		newRateData,
		rateData,
	]);

	useEffect(() => {
		if (parkerId) {
			getParkerTerminationDate({
				variables: { parkerId, tenantSubscriptionId },
				context: { apiVersion: 'v2' },
				fetchPolicy: 'network-only',
			});
		}
	}, [parkerId, getParkerTerminationDate, tenantSubscriptionId]);

	const terminationDate = terminationData?.getParkerTerminationDate || '';

	const subscriptionRateOptions =
		newBillingStartDate && usesDefaultRates && defaultRatesData
			? defaultRatesData?.listDefaultRatesBySubscription
			: newBillingStartDate
				? (newRateData?.getRatesBySubscription ?? []).filter((rate) => {
						if (rate.childRateStartDate) {
							const childRateStartDate = dayjs(rate.childRateStartDate);
							const childRateEndDate = rate.childRateEndDate
								? dayjs(rate.childRateEndDate)
								: dayjs().add(1, 'years');

							const isBillingStartDateBetweenChildRateStartAndEnd = dayjs(newBillingStartDate).isBetween(
								childRateStartDate,
								childRateEndDate,
								'day',
								'[]',
							);

							return isBillingStartDateBetweenChildRateStartAndEnd;
						}

						const activeDate = dayjs.utc(rate.activeDate);
						const billingStartDate = dayjs.tz(newBillingStartDate, facilityTimezone);

						// This is for parkers that are on a rate created from a default rate - PaTim
						if (rate.id === newRateId) {
							return true;
						}

						return activeDate.isSameOrBefore(billingStartDate, 'second');
					})
				: [];

	const rateOptions =
		parkerPendingOrNotInvoiced && !usesDefaultRates
			? subscriptionRateOptions
			: rateData?.getRate
				? [rateData.getRate]
				: defaultRatesData?.listDefaultRatesBySubscription
					? defaultRatesData.listDefaultRatesBySubscription
					: [];

	const billingStartDateDisabled = !parkerPendingOrNotInvoiced;
	const noRatesAvailable = !rateOptions.length;
	const rateDisabled =
		!parkerPendingOrNotInvoiced || !usesDefaultRates
			? !newRateData?.getRatesBySubscription.length
			: !defaultRatesData?.listDefaultRatesBySubscription.length;
	const ratesLoaded = !!newRateData;
	const invalidBillingStartDate = ratesLoaded && rateDisabled && newBillingStartDate;

	const showBackBillingMessage =
		isPendingAndNotLoading(parkerLoading, parkerIsPending) &&
		isStartDateInPreviousParkingPeriod(newBillingStartDate, facilityTimezone) &&
		!prohibitPreviousParkingPeriodParkers;

	const chooseNewRate = (e) => {
		setHasUnsavedChanges(true);
		setNewRateId(e.target.value);
		const selectedRate = rateOptions.find((rate) => rate.id === e.target.value);
		setSelectedRateIsSoldOut(selectedRate.isSoldOut);
		if (process.env.NEXT_PUBLIC_APP_NAME === 'TENANT_PORTAL') {
			// get available sublots by selected rate id
			const { tenantRateSublotSelections } = selectedRate;
			const mappedSublots = tenantRateSublotSelections.map((sel) => ({
				...sel.sublot,
			}));
			const sublotExists = mappedSublots.some((sublot) => sublot.id === selectedRate.sublotId);

			setSublotId(sublotExists ? selectedRate.sublotId.toString() : null);
			setSublotState(mappedSublots);
			return;
		}
		setSublotId(selectedRate.sublotId.toString());
	};

	return (
		<Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
			<Box className='pca__parkerDetail-rate'>
				<h2>Rate</h2>
				{showBackBillingMessage && (
					<Typography style={{ color: 'red' }}>
						*You will have the option to create back bill invoices when this parker is activated.
					</Typography>
				)}
				<Grid container spacing={2} sx={{ mt: 1 }}>
					<Grid item xs={3}>
						{!isEditMode || billingStartDateDisabled ? (
							<TextField
								required
								id='start-date'
								label='Billing Start Date'
								value={dayjs.utc(newBillingStartDate).format('MM/DD/YYYY')}
								style={{ width: '100%' }}
								variant='filled'
								InputProps={{
									readOnly: !isEditMode,
								}}
							/>
						) : (
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker<Dayjs>
									label='Billing Start Date *'
									value={dayjs(newBillingStartDate)}
									minDate={minStartDate}
									onChange={(date) => {
										const now = dayjs();

										const updatedDate = date
											.set('hour', now.hour())
											.set('minute', now.minute())
											.set('second', now.second())
											.set('millisecond', now.millisecond());

										setHasUnsavedChanges(true);
										setNewBillingStartDate(updatedDate);
										if (!usesDefaultRates) {
											setNewRateId(null); // Reset rate when billing start date is changed
										}
									}}
									sx={{
										width: '100%',
										...(invalidBillingStartDate && {
											// Outline in red when invalid
											label: { color: 'red' },
											'& .MuiOutlinedInput-root': {
												'& fieldset': {
													borderColor: 'red',
												},
												'&:hover fieldset': {
													borderColor: 'red',
												},
											},
										}),
									}}
									slotProps={{
										textField: {
											inputProps: {
												'data-cy': 'billing-start-date-date-picker',
											},
										},
									}}
								/>
							</LocalizationProvider>
						)}
					</Grid>
					<Grid item xs={3}>
						<FormControl style={{ width: '100%' }}>
							<ControlledTooltip
								title='No rates available for the selected billing start date'
								placement='top'
								arrow
								isSelectComponent
								disabled={!noRatesAvailable}
							>
								<Autocomplete
									options={rateOptions}
									getOptionLabel={(option) => option.name || ''}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									value={rateOptions.find((r) => r.id === newRateId) || null}
									onChange={(event, option) => {
										chooseNewRate({
											target: {
												name: 'rateId',
												value: option?.id ?? '',
											},
										});
									}}
									disabled={!((parkerPendingOrNotInvoiced && isEditMode) || newMode)}
									renderInput={(params) => (
										<TextField
											{...params}
											label='Active Rate'
											required
											inputProps={{
												...params.inputProps,
												readOnly: !!parkerId,
												'data-cy': 'rate-select-menu',
											}}
										/>
									)}
									renderOption={(props, option) => (
										<li {...props} key={option.id}>
											{option.name}
										</li>
									)}
								/>
							</ControlledTooltip>
							{selectedRateIsSoldOut && (
								<span style={{ fontSize: '12px', color: 'red' }}>
									The selected rate is currently sold out and the parker will be added to the waitlist
								</span>
							)}
						</FormControl>
					</Grid>
					{usesDefaultRates && newMode && !selectedRateIsSoldOut && (
						<Grid item xs={3}>
							<TextField
								id='outlined-start-adornment'
								variant='outlined'
								fullWidth
								size='medium'
								label='Monthly Gross Fee Price *'
								InputProps={{
									startAdornment: <InputAdornment position='start'>$</InputAdornment>,
									sx: { paddingLeft: '5px' },
								}}
								onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
								inputProps={{
									step: 0.01,
									min: 0,
									pattern: '[0-9]*',
									inputMode: 'numeric',
									'data-cy': 'fee-input-input',
								}}
								type='number'
								onKeyDown={(event) => {
									if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
										event.preventDefault();
									}
								}}
								value={monthlyFeePrice}
								onChange={(e: any) => setMonthlyFeePrice(Number(e.target.value))}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
					)}
					{!selectedRateIsSoldOut && (
						<Grid item xs={3}>
							<FormControl style={{ width: '100%' }}>
								<InputLabel required id='sublot-label'>
									Active Sublot
								</InputLabel>
								<Select
									label='Active Sublot'
									labelId='sublot-label'
									id='selected-sublot'
									name='sublotId'
									value={sublotId || ''}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setSublotId(e.target.value);
									}}
									disabled={process.env.NEXT_PUBLIC_APP_NAME === 'TENANT_PORTAL' && !newRateId}
									autoComplete='off'
									inputProps={{ readOnly: !isEditMode || !parkerPendingOrNotInvoiced }}
									data-cy='sublot-select-menu'
								>
									{sublotState && sublotState.length > 0 ? (
										sublotState.map((sublot) => (
											<MenuItem key={`sublot-${sublot.id}`} value={sublot.id.toString()}>
												{sublot.name}
											</MenuItem>
										))
									) : (
										<MenuItem disabled>No available sublots</MenuItem>
									)}
								</Select>
							</FormControl>
						</Grid>
					)}
					{!isEditMode && (
						<Grid item xs={3}>
							<TextField
								id='termination-date'
								label='Termination Date'
								value={formatTerminationDate(terminationDate) || ''}
								style={{ width: '100%' }}
								variant='filled'
								disabled
								InputProps={{
									readOnly: true,
								}}
								data-cy='termination-date-input'
							/>
						</Grid>
					)}
				</Grid>
			</Box>
		</Grid>
	);
};
